import { useState } from "react";
import { Notice } from "../../design/components";
import {
  useEntities,
  useSetWorkerCeiling,
  useSettings,
  useUpdateSetting,
  useWorkerAllocation,
} from "../../api/queries";
import { dateTime } from "../../lib/format";
import type { SettingCard } from "../../api/types";
import styles from "./settings.module.css";

const LABELS: Record<string, string> = {
  claim_floor: "Claim floor",
  claim_limit: "Claim limit",
  asp_calls_per_second: "ASP calls per second",
  tick_seconds: "Tick seconds",
};

const HELP: Record<string, string> = {
  claim_floor: "Every company with due work gets at least this many claims each tick, before the rest is shared out by backlog size.",
  claim_limit: "At most this many documents are claimed across every company, each tick.",
  asp_calls_per_second: "Shared by submit and outcome-checking — the hub's total call rate to the service provider.",
  tick_seconds: "How often the scheduler ticks. Raising it only slows how promptly new work is noticed — it adds no processing capacity. To handle more volume, raise a company's worker ceiling or grow the deployed pool instead.",
};

function SettingRow({ setting }: { setting: SettingCard }) {
  const [draft, setDraft] = useState(String(setting.value));
  const update = useUpdateSetting();
  const dirty = draft !== String(setting.value);

  return (
    <tr>
      <td>
        <div className={styles.name}>{LABELS[setting.key] ?? setting.key}</div>
        <div className={styles.help}>{HELP[setting.key] ?? ""}</div>
      </td>
      <td className="mono nowrap">
        {setting.minimum}–{setting.maximum}
      </td>
      <td>
        <div className="row">
          <input
            className="input"
            style={{ width: 96 }}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            aria-label={`${LABELS[setting.key] ?? setting.key} value`}
          />
          <button
            type="button"
            className="btn btn-sm"
            disabled={!dirty || update.isPending}
            onClick={() => {
              const value = Number(draft);
              if (!Number.isFinite(value)) return;
              update.mutate(
                { key: setting.key, value },
                { onError: () => setDraft(String(setting.value)) },
              );
            }}
          >
            Save
          </button>
        </div>
        {update.isError && update.variables?.key === setting.key ? (
          <Notice kind="fail">
            Must be between {setting.minimum} and {setting.maximum}.
          </Notice>
        ) : null}
      </td>
      <td className="nowrap">{setting.updatedAt ? dateTime(setting.updatedAt) : "Environment default"}</td>
    </tr>
  );
}

/** HUB-I09: a per-company cap, editable per row; adding a new one picks a company that
 * does not have one yet. Never a control over the deployed pool itself. */
function WorkerAllocationPanel() {
  const allocation = useWorkerAllocation();
  const entities = useEntities();
  const setCeiling = useSetWorkerCeiling();
  const [draftEntityId, setDraftEntityId] = useState("");
  const [draftCeiling, setDraftCeiling] = useState("");

  if (allocation.isError) return <Notice kind="fail">Worker allocation could not be loaded.</Notice>;
  if (!allocation.data) return <p className={styles.muted}>Loading…</p>;

  const { items, currentPipelineReplicas } = allocation.data;
  const sum = items.reduce((total, item) => total + item.ceiling, 0);
  const remaining = currentPipelineReplicas - sum;
  const overCommitted = remaining < 0;
  const ceilingedIds = new Set(items.map((item) => item.entityId));
  const available = (entities.data ?? []).filter((entity) => !ceilingedIds.has(entity.id));
  const draftCeilingNumber = Number(draftCeiling);
  const hasDraftCeiling = draftCeiling !== "" && Number.isFinite(draftCeilingNumber) && draftCeilingNumber > 0;
  const previewSum = sum + (hasDraftCeiling ? draftCeilingNumber : 0);
  const previewOverCommitted = previewSum > currentPipelineReplicas;

  return (
    <section className="card card-tight">
      <div className="card-head">
        <h2 className="grow">Worker allocation</h2>
        <span className={overCommitted ? styles.overCommitted : styles.muted}>
          {sum} of {currentPipelineReplicas} deployed replicas assigned —{" "}
          {overCommitted ? `${-remaining} over capacity` : `${remaining} available`}
        </span>
      </div>
      <div className="table-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Company</th>
              <th>Ceiling</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.entityId}>
                <td>{item.legalName}</td>
                <td className="mono">{item.ceiling}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={setCeiling.isPending}
                    onClick={() => setCeiling.mutate({ entityId: item.entityId, ceiling: null })}
                  >
                    Clear
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 ? (
              <tr>
                <td colSpan={3} className={styles.muted}>
                  No company has a ceiling — every company shares the pool automatically.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {available.length > 0 ? (
        <div className={styles.addRow}>
          <select
            className="select"
            value={draftEntityId}
            onChange={(event) => setDraftEntityId(event.target.value)}
            aria-label="Company to give a worker ceiling"
          >
            <option value="">Add a company…</option>
            {available.map((entity) => (
              <option key={entity.id} value={entity.id}>
                {entity.legalName}
              </option>
            ))}
          </select>
          <input
            className="input"
            style={{ width: 80 }}
            placeholder="Ceiling"
            value={draftCeiling}
            onChange={(event) => setDraftCeiling(event.target.value)}
            aria-label="New worker ceiling"
          />
          <button
            type="button"
            className="btn btn-sm"
            disabled={!draftEntityId || !draftCeiling || setCeiling.isPending}
            onClick={() => {
              const ceiling = Number(draftCeiling);
              if (!Number.isFinite(ceiling) || ceiling <= 0) return;
              setCeiling.mutate(
                { entityId: draftEntityId, ceiling },
                { onSuccess: () => { setDraftEntityId(""); setDraftCeiling(""); } },
              );
            }}
          >
            Set
          </button>
          {hasDraftCeiling ? (
            <span className={previewOverCommitted ? styles.overCommitted : styles.muted}>
              would become {previewSum} of {currentPipelineReplicas}
            </span>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export function SettingsPage() {
  const settings = useSettings();

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1 className="display">Operational settings</h1>
          <p className={styles.lede}>
            Live-editable knobs, read fresh every tick — a change here needs no restart. Growing capacity is a
            deployment action (a company&apos;s worker ceiling, or the deployed pool), never a setting on this page.
          </p>
        </div>
      </div>

      {settings.isError ? (
        <Notice kind="fail">Settings could not be loaded.</Notice>
      ) : !settings.data ? (
        <p className={styles.muted}>Loading…</p>
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Setting</th>
                <th>Allowed range</th>
                <th>Value</th>
                <th>Last changed</th>
              </tr>
            </thead>
            <tbody>
              {settings.data.items.map((item) => (
                <SettingRow key={item.key} setting={item} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <WorkerAllocationPanel />
    </>
  );
}
